/**
 * Per-chain SRC-20 registry — see spec §6.5.
 *
 * Every Seismic-specific value (reader contract address, explorer URL, decimals
 * fallback, Safe domain version) lives here. Adding mainnet = adding one entry;
 * no other code change is required.
 *
 * When CGW gains first-class `src20` fields on chain configs, the lookup in
 * this module should be moved behind `useSrc20Config(chainId)` reading from
 * `chain.src20Config` — the consumer surface stays stable.
 */
import type { Address } from 'viem'
import type { Src20TokenInfo } from './types'

export interface Src20ChainConfig {
  chainId: number
  /** Owner-gated balance reader contract — see spec §16. */
  readerAddress: Address
  /** Explorer base URL serving the SRC-20 token list endpoint. */
  explorerBaseUrl: string
  /** Path template for the per-Safe SRC-20 token list. `{address}` is replaced. */
  explorerTokenListPath: string
  /** Optional explorer link template for a token page (used by the [↗] row action). `{address}` is replaced. */
  explorerTokenPagePath?: string
  /** Optional base URL for the human-facing explorer website (token page links). Falls back to explorerBaseUrl. */
  explorerTokenPageBaseUrl?: string
  /** Default decimals when the explorer omits the field. */
  defaultDecimals: number
  /**
   * Safe domain version label for the SafeTxHash domain (e.g. '1.3.0').
   * Used by the SEND flow only — the reader has no EIP-712 domain.
   */
  safeDomainVersion: '1.3.0' | '1.4.1'
  /** Optional pre-approved well-known SRC-20s, displayed before the explorer call resolves. */
  seedTokens?: Array<Pick<Src20TokenInfo, 'address' | 'symbol' | 'name' | 'decimals' | 'iconUrl'>>
  /** Optional per-chain decoder for non-SocialScan-shaped explorer responses. */
  explorerListDecoder?: (raw: unknown) => Src20TokenInfo[]
  /** Fallback icon URL used when neither the explorer nor seed entry provides one. */
  defaultTokenIconUrl?: string
  /** Whether the chain is enabled in production. Mainnet ships as `false` until ready. */
  enabled: boolean
}

/**
 * Reader address for chain 5124 from spec §6.5 / §16. Do NOT replace with the
 * PoC's obsolete 4-argument reader `0xCf007a50F091425b038d940Fb9388eC7b8311c0c`
 * — that targets the old contract and is incompatible with the §7.1 `READER_ABI`.
 */
export const SRC20_REGISTRY: Record<number, Src20ChainConfig> = {
  5124: {
    chainId: 5124,
    readerAddress: '0x9DaFaBBc4a8F4d868d53fCC380EA1C66DDb64a4A',
    explorerBaseUrl: 'https://api.socialscan.io/seismic-testnet/v1/explorer',
    explorerTokenListPath: '/address/{address}/src20_tokens',
    explorerTokenPageBaseUrl: 'https://seismic-testnet.socialscan.io',
    explorerTokenPagePath: '/src20token/{address}',
    defaultDecimals: 18,
    safeDomainVersion: '1.3.0',
    enabled: true,
  },
}

export const getSrc20Config = (chainId: number | string | undefined): Src20ChainConfig | undefined => {
  if (chainId === undefined || chainId === null || chainId === '') return undefined
  const cfg = SRC20_REGISTRY[Number(chainId)]
  return cfg?.enabled ? cfg : undefined
}

export const hasSrc20Registry = (chainId: number | string | undefined): boolean => !!getSrc20Config(chainId)

/** Build the explorer URL for the per-Safe token list. */
export const buildTokenListUrl = (cfg: Src20ChainConfig, safeAddress: string): string =>
  cfg.explorerBaseUrl.replace(/\/$/, '') + cfg.explorerTokenListPath.replace('{address}', safeAddress)

/** Build the explorer URL for a token page, or `undefined` when not configured. */
export const buildTokenPageUrl = (cfg: Src20ChainConfig, tokenAddress: string): string | undefined => {
  if (!cfg.explorerTokenPagePath) return undefined
  const base = cfg.explorerTokenPageBaseUrl ?? cfg.explorerBaseUrl
  return base.replace(/\/$/, '') + cfg.explorerTokenPagePath.replace('{address}', tokenAddress)
}
